import React, { Component } from 'react';
import { styled } from '@mui/material/styles';
import { isEqual, cloneDeep } from 'lodash';
import { withFormsy } from 'formsy-react';
import { DropzoneDialogBase } from 'react-mui-dropzone';
import Alert from '@mui/lab/Alert';
import Button from '@mui/material/Button';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import PropTypes from 'prop-types';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import clsx from 'clsx';
import { withTranslation } from 'react-i18next';

const PREFIX = 'FileUploadFormsy';

const classes = {
  root: `${PREFIX}-root`,
  chip: `${PREFIX}-chip`,
  existing: `${PREFIX}-existing`,
  selected: `${PREFIX}-selected`,
};

const Root = styled('div')({
  [`&.${classes.root}`]: {
    '& > label': {
      marginBottom: 15,
    },
    '& > button': {
      marginRight: 15,
    },
  },
  [`& .${classes.chip}`]: {
    marginRight: 5,
  },
  [`& .${classes.existing}`]: {
    // existing file chips
  },
  [`& .${classes.selected}`]: {
    // selected file chips
  },
});

class FileUploader extends Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
      files: [],
      deleteIds: [],
    };
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps.existingFiles, this.props.existingFiles)) {
      this.setState({
        open: false,
        files: [],
      });
    }
  }

  handleClose = () => {
    this.setState({
      open: false,
    });
  };

  handleOpen = () => {
    if (this.props.disabled) return;
    this.setState({
      open: true,
    });
  };

  handleSave = (files) => {
    const { setValue } = this.props;
    // Saving files to state for further use and closing Modal.
    this.setState(
      {
        files: files,
        open: false,
      },
      () => {
        setValue && setValue(files);
      },
    );
  };

  handleDelete = (i) => {
    const { files } = this.state;
    files.splice(i, 1);
    this.setState({ files, open: false });
  };

  handleDeleteExisting = (existingFile) => {
    const { onDeleteFile } = this.props;
    const { id } = existingFile;
    const deleteIds = cloneDeep(this.state.deleteIds);
    deleteIds.push(id);
    this.setState({ deleteIds }, () => {
      // TODO: delete file failed
      console.log('deleting file', existingFile);
      onDeleteFile && onDeleteFile(id);
    });
  };

  renderExistingFileChips = () => {
    const { existingFiles: files, disabled } = this.props;
    const { deleteIds } = this.state;
    if (!files || files.length === 0) {
      return <span></span>;
    }
    if (Array.isArray(files)) {
      return files.map((file, i) => (
        <Chip
          key={i}
          className={clsx(classes.chip, classes.existing)}
          label={file.fileName}
          onDelete={disabled ? null : () => this.handleDeleteExisting(file)}
          deleteIcon={
            deleteIds.includes(file.id) ? <CircularProgress size={20} /> : <DeleteForeverIcon />
          }
        />
      ));
    } else {
      return <></>;
    }
  };

  renderSelectedFileChips = () => {
    const { existingFiles } = this.props;
    const { files } = this.state;
    if (files.length === 0 && (!existingFiles || existingFiles.length === 0)) {
      return <span>No File</span>;
    }
    return files.map((fileObj, i) => (
      <Chip
        key={i}
        className={clsx(classes.chip, classes.selected)}
        label={fileObj.file.name}
        onDelete={() => this.handleDelete(i)}
      />
    ));
  };

  render() {
    const {
      extensions = [],
      label,
      name,
      disabled,
      description,
      filesMax,
      maxFileSize = 25000,
      existingFiles,
      t,
    } = this.props;
    const { files } = this.state;
    const itemsLength = existingFiles?.length ?? 0;
    const newlyAdded = Object.keys(this.state.files).length ?? 0;
    const available = filesMax - itemsLength - newlyAdded;
    const allowOpen = available > 0 && !disabled;
    const acceptedFiles = extensions.map((ext) => `.${ext}`);
    return (
      <Root className={classes.root}>
        <p>{label}</p>
        <Button
          variant="contained"
          color="primary"
          onClick={allowOpen ? this.handleOpen : null}
          disabled={!allowOpen}
          startIcon={<CloudUploadIcon />}
        >
          {t('upload')}
        </Button>
        {this.renderExistingFileChips()}
        {this.renderSelectedFileChips()}
        <DropzoneDialogBase
          onAdd={(newFile) => this.setState({ files: [].concat(newFile, files) })}
          filesLimit={available}
          maxFileSize={maxFileSize * 1024} // number in KB
          cancelButtonText={t('cancel')}
          submitButtonText={t('select')}
          open={this.state.open}
          onSave={() => this.handleSave(files)}
          fileObjects={files}
          acceptedFiles={acceptedFiles}
          showPreviews={true}
          onClose={this.handleClose}
        />
        <FormHelperText id="component-helper-text-file-types">{`${t('allowed-file-types')}: ${extensions.join(', ')}`}</FormHelperText>
        {description && <FormHelperText id="component-helper-text">{description}</FormHelperText>}
      </Root>
    );
  }
}

FileUploader.propTypes = {
  extensions: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.boolean,
  filesMax: PropTypes.number,
  filesMin: PropTypes.number,
  label: PropTypes.string,
  description: PropTypes.string,
  setValue: PropTypes.func,
  classes: PropTypes.object,
  existingFiles: PropTypes.arrayOf(PropTypes.object),
  onDeleteFile: PropTypes.func,
};

const StyledFileUploader = FileUploader;

export default React.memo(withFormsy(withTranslation('default')(StyledFileUploader)));
